import AcColor from 'ac-colors';
import { NS } from "@/common/namespaces";
import { IllustratorArtType, IllustratorColorType, IllustratorJustifcation, IllustratorTextType } from "@/common/enums";
import { OnUpdateEvent, SubscriberService, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { findImmediateChildOrAdd, findImmediateChildren } from "@/common/element-utils";
import { parsePanelColourString } from "@/common/colours";
import { PD_COLOUR_TABLE_ID, VALID_ART_TYPES_FOR_ELEMENTS, VISIBLE_ELEMENTS } from "@/common/constants";
import { coloursServiceInstance } from "./colours-service";
import { svgToHtml } from '@/components/text-editor/svg-to-html';
import { rawHtmlToSvg } from '@/components/text-editor/html-to-svg';
import { isLayerLocked, isLayerPrinted } from '@/svgcanvas/layer-tools';

type IMetadataTree = {
    artboards: IArtboardMetadata[];
    // TODO
    deliverables: {
        barcodes: IDeliverableMetadata[];
        braille: IDeliverableMetadata[];
        briefDeliverables: IDeliverableMetadata[];
        flashes: IDeliverableMetadata[];
        panels: IDeliverableMetadata[];
    };
    documentSetup: {
        width: number;
        height: number;
        outputResolution: number;
        pageOrigin: {
            h: number;
            v: number;
        };
        ruler: {
            rulerOrigin:{ 
                h: number;
                v: number;
            };
            rulerUnits: number;
        }
    }
    linkedAssets: any[];
    layers: ILayerMetadata[];
}

type IArtboardMetadata = {
    properties: {
        name: string;
        par: number;
        position: {
            left: number;
            top: number;
            right: number;
            bottom: number;
        };
        rulerOrigin: {
            h: number;
            v: number;
        };
        uuid: string;
    }

}

type ILayerMetadata = {
    name: string;
    colour: {
        red: number;
        green: number;
        blue: number;
    };
    elements: IElementMetadata[];
    dimPlacedImages: number;
    dimmedPercent: number;
    editable: number;
    isTemplate: number;
    preview: number;
    printed: number;
    selected: number;
    visible: number;
}

export type IElementMetadata = {
    xmlid: string;
    artType: IllustratorArtType;
    elements: IElementMetadata[];
    artName: {
        name: string;
        isDefault: number;
    };
    artbounds: {
        left: number;
        top: number;
        right: number;
        bottom: number;
    };
    artRotatedbounds: {
        angle: number;
        left: number;
        top: number;
        right: number;
        bottom: number;
        flags: number;
    };
    element_positional_info: {
        x: number;
        y: number;
        width: number;
        height: number;
        top: number;
        right: number;
        bottom: number;
        left: number;
    };
    text_element_type?: IllustratorTextType;
    character_metadata?: ITextFrameCharacterMetadata[];
    paragraph_metadata?: ITextFrameParagraphMetadata[];
} & IPathMetadata;

type IPathMetadata = {
    artType: IllustratorArtType.Path | IllustratorArtType.CompoundPath,
    pathStyle: ISwatchMappingFill & ISwatchMappingStroke;
}

type ISwatchMappingFill = {
    hasFill: true;
    fill: ISwatchMappingColor;
    advancedFill: number;
} | {
    hasFill: false;
    fill: {};
    advancedFill: 0;
};

type ISwatchMappingStroke = {
    hasStroke: true;
    stroke: ISwatchMappingColor;
    advancedStroke: number;
} | {
    hasStroke: false;
    stroke: {};
    advancedStroke: 0;
};

export type ITextFrameCharacterMetadata = {
    AlternateLigatures: boolean;
    AutoKernType: number;
    AutoLeading: boolean;
    AutoMydfb: boolean;
    BaseLineShift: number;
    BaselineDirection: number;
    CharacterRotation: number;
    ConnectionForms: boolean;
    ContextualLigatures: boolean;
    DiacVPos: number;
    DiacXOffset: number;
    DiacYOffset: number;
    DigitSet: number;
    DirOverride: number;
    DiscretionaryLigatures: boolean;
    FigureStyle: number;
    Fill: boolean;
    FillBackground: boolean;
    FillColor?: ISwatchMappingColor;
    FillColour: ISwatchMappingColor;
    FillFirst: boolean;
    FillOverprint: boolean;
    FillVisible: boolean;
    Font: string;
    FontBaselineOption: number;
    FontCapsOption: number;
    FontOpenTypePositionOption: number;
    FontSize: number;
    Fractions: boolean;
    HorizontalScale: number;
    IsFontSubstituted: number;
    Italics: boolean;
    JapaneseAlternateFeature: number;
    JustificationAlternates: boolean;
    Kana: boolean;
    Kashidas: number;
    Language: number;
    Leading: number;
    LeftAki: number;
    Ligature: boolean;
    LineCap: number;
    LineDashOffset: number;
    LineJoin: number;
    LineWidth: number;
    MarkYdistanceFromBaseline: number;
    MiterLimit: number;
    NoBreak: boolean;
    OldStyle: boolean;
    Ordinals: boolean;
    Ornaments: boolean;
    OverlapSwash: boolean;
    ProportionalMetrics: boolean;
    RightAki: number;
    Ruby: boolean;
    StretchedAlternates: boolean;
    StrikethroughPosition: number;
    Stroke: boolean;
    StyleRunAlignment: number;
    StylisticAlternates: boolean;
    StylisticSets: number;
    Swash: boolean;
    TCYLeftRightAdjustment: number;
    TCYUpDownAdjustment: number;
    Titling: boolean;
    Tracking: number;
    Tsume: number;
    TypefaceName: string;
    UnderlineOffset: number;
    UnderlinePosition: number;
    VerticalScale: number;
    WariChuEnabled: boolean;
    content: string;
    index: number;
    rangeEnd: number;
    rangeSize: number;
    rangeStart: number;
}

export type ITextFrameParagraphMetadata = {
    AutoHyphenate: boolean;
    AutoLeadingPercentage: number;
    AutoTCY: number;
    BurasagariType: number;
    BurniKinshi: boolean;
    ComposerEngine: number;
    ConsecutiveHyphenLimit: number;
    DefaultTabWidth: number;
    DesiredGlyphScaling: number;
    DesiredLetterSpacing: number;
    DesiredWordSpacing: number;
    EveryLineComposer: boolean;
    EndIndent: number;
    FirstLineIndent: number;
    HangingRoman: boolean;
    HyphenateCapitalized: boolean;
    HyphenatedWordSize: number;
    HyphenationPreference: number;
    HyphenationZone: number;
    KashidaWidth: number;
    KurikaeshiMojiShori: boolean;
    LeadingType: number;
    MaxGlyphScaling: number;
    MaxLetterSpacing: number;
    MaxWordSpacing: number;
    MinGlyphScaling: number;
    MinLetterSpacing: number;
    MinWordSpacing: number;
    ParagraphDirection: number;
    ParagraphJustification: IllustratorJustifcation;
    ParagraphJustificationMethod: number;
    PostHyphenSize: number;
    PreHyphenSize: number;
    PreferredKinsokuOrder: number;
    SingleWordJustification: number;
    SpaceAfter: number;
    SpaceBefore: number;
    StartIndent: number;
    content: string;
    index: number;
    rangeEnd: number;
    rangeSize: number;
    rangeStart: number;
}

export type ISwatchMappingColor = {
    overprint: boolean;
    colour: {
        colourType: IllustratorColorType;
    }
} & ({
    colour: {
        colourType: IllustratorColorType.CustomColour;
        colourHint: {
            c: number,
            m: number;
            y: number;
            k: number;
        };
        colour: {
            IS_PROCESS_COLOUR: boolean;
            NAME: string
            colourType: IllustratorColorType.Cmyk;
            c: number;
            m: number;
            y: number;
            k: number;
            tint: number;
        }
    }

});

type IDeliverableMetadata = {
    field_Name: string;
    identifier: string;
    xmlid: string;
}

function CreateEmptyMetadataStructure() {
    const ret: IMetadataTree = {
        artboards: [],
        deliverables: {
            barcodes: [],
            braille: [],
            briefDeliverables: [],
            flashes: [],
            panels: [],
        },
        documentSetup: {
            width: 0,
            height: 0,
            outputResolution: 0,
            pageOrigin: {
                h: 0,
                v: 0,
            },
            ruler: {
                rulerOrigin: {
                    h: 0,
                    v: 0,
                },
                rulerUnits: 0,
            },
        },
        linkedAssets: [],
        layers: [],
    };

    return ret;
}

/**
 * Finds the document's root <metadata> element (the one holding the XMP). The Panel Designer colour table is
 * also stored as a root <metadata> element, so it is skipped.
 * @param svgRoot The SVG root element to search.
 * @returns The document metadata element, or null if there is none.
 */
export function findDocumentMetadataElement(svgRoot: SVGSVGElement): SVGMetadataElement | null {
    for (const child of svgRoot.children) {
        if (child.tagName.toLowerCase() === "metadata" && child.getAttribute("id") !== PD_COLOUR_TABLE_ID) {
            return child as SVGMetadataElement;
        }
    }

    return null;
}

export class MetadataService extends SubscriberService<MetadataService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return metadataServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public constructor() {
        super();
        (<any>window).metadataService = this;
    }

    private _documentRootMetadata: SVGMetadataElement;
    private documentTridentMetadata: IMetadataTree = CreateEmptyMetadataStructure();
    private elementMetadataCacheMap: Map<string, IElementMetadata> = new Map();

    get metadataRoot() {
        if (!this._documentRootMetadata) {
            this._documentRootMetadata = document.createElementNS(NS.SVG, "metadata");
        }

        return this._documentRootMetadata;
    }

    get xmpmetaRoot() {
        return findImmediateChildOrAdd(this.metadataRoot, NS.X, "x:xmpmeta", (meta) => {
            meta.setAttribute("xmlns:x", NS.X_Alias);
            meta.setAttribute("x:xmptk", "Adobe XMP Core 7.2-c000 79.1b65a79, 2022/06/13-17:46:14");
            return meta;
        });
    }

    get rdfRoot() {
        return findImmediateChildOrAdd(this.xmpmetaRoot, NS.RDF, "rdf:rdf", (meta) => {
            meta.setAttribute("xmlns:rdf", NS.RDF);
            return meta;
        });
    }

    get rdfRootDescription() {
        return findImmediateChildOrAdd(this.rdfRoot, NS.RDF, "rdf:description", (meta) => {
            meta.setAttribute("xmlns:zaa", NS.ZAA);
            meta.setAttribute("xmlns:dc", NS.DC);
            meta.setAttribute("xmlns:pdf", NS.PDF);
            meta.setAttribute("xmlns:xmp", NS.XMP);
            meta.setAttribute("xmlns:xmpGImg", NS.XMP_GIMG);
            meta.setAttribute("xmlns:xmpTPg", NS.XMP_TPG);
            meta.setAttribute("xmlns:xmpG", NS.XMP_G);
            meta.setAttribute("xmlns:xmpMM", NS.XMP);
            meta.setAttribute("xmlns:stEvt", NS.ST_EVT);
            meta.setAttribute("xmlns:stRef", NS.ST_REF);
            meta.setAttribute("xmlns:stMfs", NS.ST_MFS);
            meta.setAttribute("xmlns:stDim", NS.ST_DIM);
            meta.setAttribute("xmlns:stFnt", NS.ST_FNT);
            meta.setAttribute("xmlns:illustrator", NS.ILLUSTRATOR);
            meta.setAttribute("xmlns:awl", NS.AWL);
            meta.setAttribute("xmlns:ExtensisFontSense", NS.EXTENSIS_FONT_SENSE);
            meta.setAttribute("xmlns:MercurySpecific", NS.MERCURY_SPECIFIC);
            meta.setAttribute("xmlns:MercuryEsko", NS.MERCURY_ESKO);
            meta.setAttribute("xmlns:egGr", NS.EG_GR);
            meta.setAttribute("xmlns:egInk", NS.EG_INK);
            meta.setAttribute("xmlns:egInkCovL", NS.EG_INKCOVL);
            meta.setAttribute("xmlns:egLayL", NS.EG_LAYL);
            meta.setAttribute("xmlns:egLay", NS.EG_LAY);
            meta.setAttribute("xmlns:egExtFL2", NS.EG_EXTFL2);
            meta.setAttribute("xmlns:egExtF", NS.EG_EXTF);
            meta.setAttribute("xmlns:egExtFL", NS.EG_EXTFL);
            meta.setAttribute("xmlns:egPagL", NS.EG_PAGL);
            meta.setAttribute("xmlns:egPag", NS.EG_PAG);
            meta.setAttribute("xmlns:egFontL", NS.EG_FONTL);
            meta.setAttribute("xmlns:egFont", NS.EG_FONT);

            return meta;
        });
    }

    get xmpFontsRoot() {
        return findImmediateChildOrAdd(this.rdfRootDescription, NS.XMP_TPG, "xmpTPg:Fonts", (meta) => {
            const fontsBag = document.createElementNS(NS.RDF, "rdf:Bag");
            meta.append(fontsBag);

            return meta;
        });
    }

    get xmpAllFonts() {
        return this.xmpFontsRoot.querySelectorAll("Bag li");
    }

    get swatchMappingsBag() {
        const meta = findImmediateChildOrAdd(this.rdfRootDescription, NS.ZAA, "zaa:SwatchMappings");
        return findImmediateChildOrAdd(meta, NS.RDF, "rdf:Bag");
    }

    /**
     * Grabs the <metadata> tag and removes it from the DOM for some slight perf improvements.
     * @param svgRoot The #svgcontent element.
     * @returns
     */
    public extractMetadata(svgRoot: SVGSVGElement) {
        this._documentRootMetadata = findDocumentMetadataElement(svgRoot);
        this._documentRootMetadata?.remove();

        if (!this.metadataRoot) { // Getter generates a root if we didn't find one above.
            throw new Error("Could not generate root <metadata> tag. This shouldn't happen.");
        }

        this.clearTridentMetadata();

        for (const mappingLi of this.swatchMappingsBag.children) {
            const desc = mappingLi.firstElementChild;
            if (!desc) {
                continue;
            }

            const xmlid = desc.querySelector("xmlid")?.textContent;
            if (!xmlid) {
                continue;
            }
        }

        // Tell everone about our metadata
        const ev = new CustomEvent("metadata-updated");
        window.dispatchEvent(ev);
        this._UpdateSubscribers();
        return this._documentRootMetadata;
    }

    /**
     * Re-adds previously extracted metadata (if any) to the supplied element.
     * @param svgRoot The SVG root element we're re-applying metadata to.
     */
    public reAddMetadata(svgRoot: SVGSVGElement) {
        svgRoot.prepend(this.metadataRoot);
        this._UpdateSubscribers();
    }

    /**
     * Removes the document metadata previously re-added with {@link reAddMetadata} again, e.g. once the SVG
     * has been serialised. Unlike {@link extractMetadata} this keeps the current metadata (including the Trident
     * metadata tree) and does not dispatch "metadata-updated", as nothing about the metadata has changed.
     * @param svgRoot The SVG root element the metadata was re-added to.
     */
    public detachMetadata(svgRoot: SVGSVGElement) {
        if (this._documentRootMetadata?.parentNode === svgRoot) {
            this._documentRootMetadata.remove();
        }
    }

    /**
     * Removes the XMP swatch groups and Esko inks. Spot colours are no longer stored in the SVG's XMP (artworks use
     * the Artwork DNA document, panels the pd-colour-table), and the Stamper would otherwise read stale colours.
     * Missing metadata is never created.
     */
    public removeSpotColourXmp() {
        const description = [this.metadataRoot]
            .flatMap(el => findImmediateChildren(el, "x:xmpmeta"))
            .flatMap(el => findImmediateChildren(el, "rdf:rdf"))
            .flatMap(el => findImmediateChildren(el, "rdf:description"));

        for (const el of description.flatMap(d => [...findImmediateChildren(d, "xmpTPg:SwatchGroups"), ...findImmediateChildren(d, "egGr:inks")])) {
            el.remove();
        }
    }

    public setTridentMetadata(metadata: any) {
        this.documentTridentMetadata = metadata;
        this.fixupElementIds();
        this.fixupAllTextElementsFromMetadata();
    }

    public getTridentMetadata() {
        this.syncLayerMetadataFromDocument();
        return this.documentTridentMetadata;
    }

    public clearTridentMetadata() {
        this.documentTridentMetadata = CreateEmptyMetadataStructure();
    }

    public getTridentDeliverablesObject(deliverableType: "barcode" | "braille" | "briefdeliverables" | "panels") {
        switch(deliverableType) {
            case "barcode":
                return this.documentTridentMetadata.deliverables.barcodes;
            case "braille":
                return this.documentTridentMetadata.deliverables.braille;
            case "briefdeliverables":
                return this.documentTridentMetadata.deliverables.briefDeliverables;
            case "panels":
                return this.documentTridentMetadata.deliverables.panels;
            default:
                throw new Error(`Unknown deliverable type '${deliverableType}'`);
        }
    }

    public getMetadataForLayer(layerName: string): ILayerMetadata | null {
        for (const layer of this.documentTridentMetadata.layers) {
            if (layer.name === layerName) {
                this.syncLayerMetadata(layer);
                return layer;
            }
        }

        return null;
    }

    public getOrCreateMetadataForLayer(layerName: string): ILayerMetadata {
        const layerMetadata = this.getMetadataForLayer(layerName);
        if(layerMetadata) {
            return layerMetadata;
        }

        const randCol = AcColor.hslToRgb([Math.random() * 360, 100.0, 50.0]); // NOSONAR(S2245): This is a random colour, it's not a security issue.
        const newLayer: ILayerMetadata = {
            name: layerName,
            colour: {
                red: randCol[0],
                green: randCol[1],
                blue: randCol[2],
            },
            elements: [],
            dimPlacedImages: 0,
            dimmedPercent: 0,
            editable: 1,
            isTemplate: 0,
            preview: 0,
            printed: 1,
            selected: 0,
            visible: 1,
        };

        this.documentTridentMetadata.layers.push(newLayer);
        this.syncLayerMetadata(newLayer);
        return newLayer;
    }

    private getLayerTitleElement(layerElement: Element): SVGTitleElement | null {
        const firstChild = layerElement.firstElementChild;
        if (firstChild instanceof SVGTitleElement) {
            return firstChild;
        }

        return layerElement.querySelector("title");
    }

    private findLayerElement(layerName: string): SVGGElement | null {
        const svgRoot = document.getElementById("svgcontent");
        if (!(svgRoot instanceof SVGSVGElement)) {
            return null;
        }

        for (const layerElement of svgRoot.querySelectorAll("g.layer")) {
            const titleElement = this.getLayerTitleElement(layerElement);
            if (titleElement?.innerHTML === layerName) {
                return layerElement;
            }
        }

        return null;
    }

    private syncLayerMetadata(layerMetadata: ILayerMetadata) {
        const layerElement = this.findLayerElement(layerMetadata.name);
        if (!layerElement) {
            return;
        }

        layerMetadata.visible = layerElement.getAttribute("display") === "none" ? 0 : 1;
        layerMetadata.editable = isLayerLocked(layerElement) ? 0 : 1;
        layerMetadata.printed = isLayerPrinted(layerElement) ? 1 : 0;
    }

    private syncLayerMetadataFromDocument() {
        const svgRoot = document.getElementById("svgcontent");
        if (!(svgRoot instanceof SVGSVGElement)) {
            return;
        }

        const syncedLayers: ILayerMetadata[] = [];
        for (const layerElement of svgRoot.querySelectorAll("g.layer")) {
            const titleElement = this.getLayerTitleElement(layerElement);
            const layerName = titleElement?.innerHTML;
            if (!layerName) {
                continue;
            }

            const layerMetadata = this.getOrCreateMetadataForLayer(layerName);
            this.syncLayerMetadata(layerMetadata);
            syncedLayers.push(layerMetadata);
        }

        this.documentTridentMetadata.layers = syncedLayers;
    }

    /**
     * Gets the metadata for the given element, if it exists.
     * @param element The SVG element to get metadata for.
     * @param strict If true, will only return metadata if it exists for the given element. If false, will attempt to find metadata 
     *               for the parent element if none exists for the given element and the given element has no siblings. This can happen
     *               if an element is placed within a clipping group by Illustrator's SVG exporter.
     */
    public getMetadataForElement(element: Element, strict: boolean = false): IElementMetadata | null {
        function findElement(metadataEntries: IElementMetadata[]) {
            if (!metadataEntries) {
                return null;
            }

            for(const entry of metadataEntries) {
                if(entry.xmlid === element.id) {
                    return entry;
                }

                const found = findElement(entry.elements);
                if(found) {
                    return found;
                }
            }

            return null;
        }

        for (const layer of this.documentTridentMetadata.layers) {
            const found = findElement(layer.elements);

            if(found) {
                return found;
            }
        }

        if( element instanceof SVGTSpanElement 
            || element instanceof SVGTextPathElement
            || (!strict && !element.previousElementSibling && !element.nextElementSibling)
        ) {
            let meta = this.getMetadataForElement(element.parentElement);
            if (meta && meta.artType === IllustratorArtType.Group && meta.elements.length > 0) {
                for(const el of meta.elements) {
                    if(el.artType === IllustratorArtType.TextFrameArt) {
                        meta = el;
                        break;
                    }
                }
            }

            return meta;
        }
    }

    public getOrCreateMetadataForElement(element: SVGElement): IElementMetadata {
        let metadata = this.getMetadataForElement(element);
        if (metadata) {
            return metadata;
        }

        metadata = {
            xmlid: element.id,
            artType: 1,
            hasFill: false,
            fill: {},
            hasStroke: false,
            stroke: {},
        } as IElementMetadata;

        let parentMetadata: IElementMetadata | ILayerMetadata;
        const parent = element.parentElement;

        if(parent instanceof SVGGElement && parent.classList.contains("layer")) {
            parentMetadata = this.getOrCreateMetadataForLayer(parent.firstElementChild.innerText);
        } else {
            parentMetadata = this.getOrCreateMetadataForElement(element.parentElement);
        }
        
        if(!parentMetadata.elements) {
            parentMetadata.elements = [];
        }

        parentMetadata.elements.push(metadata);
        return this.updateElementMetadataForElement(element);
    }

    public updateElementMetadataForElement(element: SVGElement): IElementMetadata {
        const metadata = this.getOrCreateMetadataForElement(element);

        if ('pathStyle' in metadata) {
            ["fill", "stroke"].forEach((attr) => {
                const attrUpper = attr[0].toUpperCase() + attr.substring(1);
                const peAttr = `pe-${attr}`;
                const hasAttr = element.hasAttribute(peAttr);

                if(!hasAttr && metadata.pathStyle[`has${attrUpper}`] === true) {
                    // The SVG lacks the attribute, but the metadata says it should be there. Do nothing.
                    return;
                }

                metadata.pathStyle[`has${attrUpper}`] = hasAttr;

                if (!hasAttr) {
                    metadata.pathStyle[attr] = {};
                } else {
                    const parsedCol = parsePanelColourString(element.getAttribute(peAttr));

                    switch (parsedCol.type) {
                        case "registration": {
                            metadata.pathStyle[attr] = {
                                colour: {
                                    colourType: IllustratorColorType.CustomColour,
                                    colour: {
                                        IS_PROCESS_COLOUR: true,
                                        NAME: "[Registration]",
                                        colourType: IllustratorColorType.Cmyk,
                                        c: parsedCol.reg,
                                        m: parsedCol.reg,
                                        y: parsedCol.reg,
                                        k: parsedCol.reg,
                                        tint: parsedCol.alpha,
                                    },
                                    colourHint: {
                                        c: parsedCol.reg,
                                        m: parsedCol.reg,
                                        y: parsedCol.reg,
                                        k: parsedCol.reg,
                                    }
                                },
                                overprint: false, // TODO
                            } as ISwatchMappingColor;
                            break;

                        } case "spotcol": {
                            const spotName = coloursServiceInstance.GetSpotColourWithIdOrName(parsedCol.spotID)?.name
                                ?? parsedCol.spotID;

                            metadata.pathStyle[attr] = {
                                colour: {
                                    colourType: IllustratorColorType.CustomColour,
                                    colour: {
                                        IS_PROCESS_COLOUR: false,
                                        NAME: spotName,
                                        colourType: IllustratorColorType.Cmyk,
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                        tint: parsedCol.alpha,
                                    },
                                    colourHint: {
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                    }
                                },
                                overprint: false, // TODO
                            } as ISwatchMappingColor;
                            break;

                        } case "customspot": {
                            // TODO: How to represent?
                            metadata.pathStyle[attr] = {
                                colour: {
                                    colourType: IllustratorColorType.CustomColour,
                                    colour: {
                                        IS_PROCESS_COLOUR: false,
                                        NAME: parsedCol.name,
                                        colourType: IllustratorColorType.Cmyk,
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                        tint: parsedCol.alpha,
                                    },
                                    colourHint: {
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                    }
                                },
                                overprint: false, // TODO
                            } as ISwatchMappingColor;
                            break;

                        } case "processcol": {
                            // TODO: Alpha?
                            metadata.pathStyle[attr] = {
                                colour: {
                                    colourType: IllustratorColorType.Cmyk,
                                    colour: {
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                    },
                                    colourHint: {
                                        c: parsedCol.c,
                                        m: parsedCol.m,
                                        y: parsedCol.y,
                                        k: parsedCol.k,
                                    },
                                },
                                overprint: false, // TODO
                            } as ISwatchMappingColor;
                            break;

                        } case "none": {
                            metadata[`has${attrUpper}`] = "false";
                            metadata[attr] = {};
                            break;
                        }
                    }
                }
            });
        }

        return metadata;
    }

    public fixupElementIds() {
        const svgRoot = document.getElementById("svgcontent");
        const elementsWithoutIdSelector = VISIBLE_ELEMENTS.map(el => `${el}:not([id])`).join(",");
        const elementsWithoutId = svgRoot.querySelectorAll(elementsWithoutIdSelector);
        
        for(const el of elementsWithoutId) {
            if(el.id) {
                // Element gained an ID from a prior loop.
                continue;
            }

            // TODO: Disabled for now as it looks like the SVG export is fixed once more.
            // this.tryResolveElementId(el as SVGElement);
        }
    }

    public tryResolveElementId(element: SVGElement) {
        if(element.id) {
            console.log(`[Element ID Fixup] Element with ID '${el.id}' does not need to have its ID fixed. Skipping.`);
            return true;
        }

        const resolveElementIdRecursive = (el: SVGElement): boolean => {
            if(el.id) {
                // Element already has an ID. We can early out as the parent caller is 
                // guaranteed to get a metadata entry (if any exists).
                return;
            }

            const parent = el.parentElement;
            if(!parent) {
                console.error(`[Element ID Fixup] Error: Somehow reached the top of the DOM while trying to fixup element ID for`, element);
                debugger;
                return false;
            }
            const parentChildren = Array.from(parent.children)
                .filter(e => VISIBLE_ELEMENTS.includes(e.tagName.toLowerCase())) // Only visible elements tend to be stored in metadata.
                .toReversed(); // Metadata order is reversed from SVG order, so reverse the list for easier indexing.

            const elIndex = parentChildren.indexOf(el);
            let elementsList: IElementMetadata[] = null;
            if(parent instanceof SVGGElement && parent.classList.contains("layer")) {
                const titleEl = parent.querySelector("title") as SVGTitleElement;
                if(!titleEl) {
                    return false;
                }
                const layerMetadata = this.getMetadataForLayer(titleEl.innerHTML);
                if(!layerMetadata) {
                    return false;
                }
                elementsList = layerMetadata.elements;
            } else {
                resolveElementIdRecursive(parent);
                const elMetadata = this.getMetadataForElement(parent, true);
                if(!elMetadata) {
                    return false;
                }
                elementsList = elMetadata.elements;
            }

            if(!elementsList) {
                return false;
            }

            // Filter out guide and utility elements that have snuck into the metadata.
            elementsList = elementsList.filter(e => !(e.artName.name == "<Guide>" && e.artName.isDefault === 1));

            if(elIndex < 0 || elIndex >= elementsList.length) {
                return false;
            }

            const elMetadata = elementsList[elIndex];
            const validArtType = VALID_ART_TYPES_FOR_ELEMENTS[el.tagName.toLowerCase()];
            if(!elMetadata || !validArtType) {
                return false;
            }

            if(!validArtType.includes(elMetadata.artType)) {
                console.warn(`[Element ID Fixup] Refusing to fixup element ID of SVG type '${el.tagName.toLowerCase()}' as matched metadata artType '${elMetadata.artType}' was not valid. Matched at index ${elIndex}.`, el, elMetadata, elementsList);
                console.log(parentChildren);
                return false;
            }

            el.id = elMetadata.xmlid;
            console.log(`[Element ID Fixup] Fixed up element ID of SVG type '${el.tagName.toLowerCase()}', metadata type '${elMetadata.artType}' to be '${el.id}'.`, el, elMetadata);
            return true;
        }

        return resolveElementIdRecursive(element);
    }
    
    public fixupAllTextElementsFromMetadata() {
        const svgRoot = document.getElementById("svgcontent");
        // Find all text and tspan elements that have an ID.
        const textElements = svgRoot.querySelectorAll("text[id]");
        
        for (const el of textElements) {
            this.fixupTextElementFromMetadata(el);
        }
    }

    public fixupTextElementFromMetadata(element: SVGTextElement | SVGTSpanElement) {
        if(element instanceof SVGTSpanElement) {
            return this.fixupTextElementFromMetadata(element.parentElement as SVGTextElement);
        }

        if (!(element instanceof SVGTextElement)) {
            console.error(`Could not perform a text fixup on element '${element.id}''. Element is not an SVG <text> element.`);
            return false;
        }

        if (!this.getMetadataForElement(element)) {
            console.error(`Could not perform a text fixup on element '${element.id}''. Element is missing a metadata entry.`);
            return false;
        }

        try {
            const parsedHtml = svgToHtml(element);
            rawHtmlToSvg(element, parsedHtml.text);
        } catch(ex) {
            console.error(`Could not fixup element '${element.id}'.`, ex);
            return false;
        }

        return true;
    }
}

export const metadataServiceInstance: MetadataService = createStaticServiceInstance(new MetadataService(), "metadataService");

// For React Functional Components
export const useMetadataService = createUseZenAutoArtService(metadataServiceInstance);
